// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.

/** @type {Record<string, (d: Date) => string>} */
const DATE_FORMATTERS = {
  annual: (d) => String(d.getFullYear()),
  quarterly: (d) => `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`,
  monthly: (d) => d.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
  weekly: (d) =>
    `Week of ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
  daily: (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
};

export class ToolData {
  specialColumns = ["map_id", "map_description", "date"];
  /** @type {Set<string>} */
  primaryColumns = new Set();
  /** @type {Set<string>} */
  detailColumns = new Set();
  /**
   * @param {Array<Record<string, any>>} rawData
   * @param {Array<Record<string, any>>} [rawDetailData]
   * @param {Array<Record<string, any>>} [rawConfigData]
   * @param {{ accentColor?: string, dateFrequency?: "annual" | "quarterly" | "monthly" | "weekly" | "daily" }} [options]
   */
  constructor(rawData, rawDetailData, rawConfigData, options = {}) {
    const { dateFrequency } = options;

    this.primaryData = this.parseData(rawData);
    for (let col in this.primaryData[0]) {
      if (!this.specialColumns.includes(col)) {
        this.primaryColumns.add(col);
      }
    }

    // Time series detection
    this.hasTimeSeries = this.primaryData.length > 0 && "date" in this.primaryData[0];

    if (this.hasTimeSeries) {
      this.primaryData.sort((a, b) => String(a.date).localeCompare(String(b.date)));

      /** @type {string[]} */
      this._timePoints = [...new Set(this.primaryData.map((r) => String(r.date)))].sort();
      this._latestDate = this._timePoints[this._timePoints.length - 1];

      this.dateFrequency = dateFrequency || "annual";
      this._dateFormatter = DATE_FORMATTERS[this.dateFrequency] || DATE_FORMATTERS.annual;
    }

    if (rawDetailData) {
      this.setDetailData(rawDetailData);
    } else {
      this.setDetailData(this.primaryData);
    }
  }

  /**
   * @param {Array<Record<string, any>>} rawData
   * @returns {Record<string, (number | string)>[]}
   */
  parseData(rawData) {
    return rawData.map((d) => {
      /** @type {Record<string, (number | string)>} */
      let result = {};
      for (const k of Object.keys(d)) {
        if (!this.specialColumns.includes(k)) {
          // try to convert value to number
          result[k] = Number(d[k]);
          // if number conversion fails, keep as string
          if (isNaN(result[k])) {
            result[k] = d[k];
          }
        } else {
          result[k] = d[k];
        }
      }
      return result;
    });
  }

  /** @param {Array<Record<string, any>>} rawData */
  setDetailData(rawData, options = {}) {
    const defaultOptions = {
      stateSummaryLabel: "Statewide",
    };

    this.detailDataOptions = Object.assign(defaultOptions, options);
    this.detailData = this.parseData(rawData);
    let dataKeys = Object.keys(this.detailData[0]);
    for (let i = 0; i < dataKeys.length; i++) {
      const col = dataKeys[i];
      if (i == 1) {
        this.labelColumn = col;
      }
      if (!this.specialColumns.includes(col)) {
        this.detailColumns.add(col);
      }
    }
  }

  getPrimaryData() {
    return this.primaryData;
  }

  getDetailData() {
    return this.detailData;
  }

  /**
   * @param {string} mapId
   * @param {boolean} [includeSummary]
   */
  getDetailDataForId(mapId, includeSummary = false) {
    let allData = this.getDetailData() || [];
    let result = allData.filter((row) => row.map_id === mapId);
    let primaryRow = this.getRow(mapId);
    if (includeSummary && this.labelColumn && primaryRow) {
      let summaryRow = Object.assign({}, primaryRow);
      summaryRow[this.labelColumn] = this.detailDataOptions?.stateSummaryLabel ?? "";
      return [summaryRow].concat(result);
    }
    return result;
  }

  getPrimaryColumns() {
    return Array.from(this.primaryColumns);
  }

  /**
   * @param {string} map_id
   * @param {string} [date] - optional date for time series data
   * @returns {Record<string, (number | string)>}
   */
  getRow(map_id, date) {
    if (!this.hasTimeSeries) {
      return this.primaryData.find((row) => row.map_id === map_id) || this.getFallback(map_id);
    }
    date = date ?? this._latestDate;
    return (
      this.primaryData.find((row) => row.map_id === map_id && row.date === date) ||
      this.getFallback(map_id)
    );
  }

  /**
   * @param {string} map_id
   * @returns {Record<string, (number | string)>}
   */
  getFallback(map_id) {
    /** @type {Record<string, string>} */
    let fallbackData = { map_id: map_id };
    this.getPrimaryColumns().forEach((col) => {
      fallbackData[col] = "N/A";
    });
    return fallbackData;
  }

  getDetailColumns() {
    return Array.from(this.detailColumns);
  }

  /**
   * Returns rows projected to the specified columns (plus map_id).
   * @param {string[]} columns
   * @param {{ source?: "primary" | "detail", mapId?: string, date?: string }} [options]
   * @returns {Record<string, (number | string)>[]}
   */
  selectColumns(columns, options = {}) {
    const { source = "primary", mapId, date } = options;
    let rows = source === "detail" ? this.detailData : this.primaryData;
    if (this.hasTimeSeries && source === "primary") {
      const filterDate = date ?? this._latestDate;
      rows = rows.filter((row) => row.date === filterDate);
    }
    if (mapId) {
      rows = rows.filter((row) => row.map_id === mapId);
    }
    return rows.map((row) => {
      let result = { map_id: row.map_id };
      if (source === "detail" && this.labelColumn && row[this.labelColumn] != null) {
        result[this.labelColumn] = row[this.labelColumn];
      }
      for (let col of columns) {
        if (col in row) result[col] = row[col];
      }
      return result;
    });
  }

  // --- Time series methods ---

  /** Get sorted unique time points as date strings */
  getTimePoints() {
    return this._timePoints || [];
  }

  /** Get the latest date string */
  getLatestDate() {
    return this._latestDate;
  }

  /**
   * Format a YYYY-MM-DD date string according to the declared frequency.
   * @param {string} dateStr
   * @returns {string}
   */
  formatDate(dateStr) {
    const [y, m, d] = dateStr.split("-").map(Number);
    return this._dateFormatter(new Date(y, m - 1, d));
  }

  /**
   * Get time points as {value, label} pairs — useful for dropdowns/sliders.
   * @returns {Array<{value: string, label: string}>}
   */
  getTimePointOptions() {
    return this.getTimePoints().map((d) => ({
      value: d,
      label: this.formatDate(d),
    }));
  }

  /**
   * Get one row per map_id for a given date (what the map needs).
   * Defaults to latest date. Returns full primaryData if no time series.
   * @param {string} [date]
   * @returns {Record<string, (number|string)>[]}
   */
  getSnapshot(date) {
    if (!this.hasTimeSeries) return this.primaryData;
    date = date ?? this._latestDate;
    return this.primaryData.filter((r) => r.date === date);
  }

  /**
   * Get all rows for a single geography across time, sorted by date.
   * @param {string} mapId
   * @returns {Record<string, (number|string)>[]}
   */
  getTimeSeries(mapId) {
    if (!this.hasTimeSeries) return [this.getRow(mapId)];
    return this.primaryData
      .filter((r) => r.map_id === mapId)
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }
}
